import { useState } from 'react';
import styles from './ApiKeys.module.css';

interface ApiKey {
	id: string;
	name: string;
	publicKey: string;
	createdAt: number;
	lastUsed: number | null;
	status: 'active' | 'revoked';
}

const mockKeys: ApiKey[] = [
	{ id: '1', name: 'Production Key', publicKey: 'pk_live_abc123xyz789', createdAt: Date.now() - 86400000 * 30, lastUsed: Date.now() - 60000, status: 'active' },
	{ id: '2', name: 'Test Key', publicKey: 'pk_test_def456uvw012', createdAt: Date.now() - 86400000 * 7, lastUsed: Date.now() - 3600000, status: 'active' },
];

export default function ApiKeys() {
	const [keys] = useState<ApiKey[]>(mockKeys);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [newKeyName, setNewKeyName] = useState('');

	const handleCreateKey = (e: React.FormEvent) => {
		e.preventDefault();
		// In real app, would call API
		setShowCreateModal(false);
		setNewKeyName('');
	};

	return (
		<div className={styles.apiKeys}>
			<header className={styles.header}>
				<div>
					<h1 className={styles.title}>API Keys</h1>
					<p className={styles.subtitle}>Manage your API keys for platform integration</p>
				</div>
				<button onClick={() => setShowCreateModal(true)} className={styles.createBtn}>
					<PlusIcon />
					Create Key
				</button>
			</header>

			{/* Integration Guide */}
			<div className={styles.infoCard}>
				<div className={styles.infoIcon}>
					<InfoIcon />
				</div>
				<div className={styles.infoContent}>
					<h3>Integration Guide</h3>
					<p>Use your API keys to authenticate requests to the Ringfort API. Include your public key in the Authorization header as a Bearer token.</p>
					<code className={styles.codeBlock}>Authorization: Bearer pk_live_abc123xyz789</code>
				</div>
			</div>

			{/* Keys List */}
			<div className={styles.keysList}>
				{keys.map((key) => (
					<div key={key.id} className={styles.keyCard}>
						<div className={styles.keyHeader}>
							<div className={styles.keyInfo}>
								<h3 className={styles.keyName}>{key.name}</h3>
								<span className={`${styles.badge} ${key.status === 'active' ? styles.badgeActive : styles.badgeRevoked}`}>{key.status}</span>
							</div>
							<button className={styles.revokeBtn}>Revoke</button>
						</div>
						<div className={styles.keyValue}>
							<code>{key.publicKey}</code>
							<button className={styles.copyBtn} title="Copy to clipboard">
								<CopyIcon />
							</button>
						</div>
						<div className={styles.keyMeta}>
							<span>Created {formatDate(key.createdAt)}</span>
							<span>•</span>
							<span>Last used {key.lastUsed ? formatTime(key.lastUsed) : 'Never'}</span>
						</div>
					</div>
				))}
			</div>

			{/* Webhook Configuration */}
			<section className={styles.section}>
				<h2 className={styles.sectionTitle}>Webhook Configuration</h2>
				<div className={styles.webhookForm}>
					<div className={styles.field}>
						<label className={styles.label}>Wallet Callback URL</label>
						<input type="url" placeholder="https://your-domain.com/api/wallet" className={styles.input} />
						<p className={styles.hint}>We'll send wallet balance/debit/credit requests to this URL</p>
					</div>
					<button className={styles.saveBtn}>Save Webhook</button>
				</div>
			</section>

			{/* Create Modal */}
			{showCreateModal && (
				<div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
					<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
						<div className={styles.modalHeader}>
							<h2>Create API Key</h2>
							<button onClick={() => setShowCreateModal(false)} className={styles.closeBtn}>
								<CloseIcon />
							</button>
						</div>
						<form onSubmit={handleCreateKey} className={styles.modalForm}>
							<div className={styles.field}>
								<label className={styles.label}>Key Name</label>
								<input type="text" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="e.g., Production Key" required className={styles.input} />
							</div>
							<div className={styles.modalActions}>
								<button type="button" onClick={() => setShowCreateModal(false)} className={styles.cancelBtn}>
									Cancel
								</button>
								<button type="submit" className={styles.submitBtn}>
									Create Key
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

function formatDate(timestamp: number): string {
	return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(timestamp: number): string {
	const diffMs = Date.now() - timestamp;
	const diffMins = Math.floor(diffMs / 60000);
	if (diffMins < 60) return `${diffMins}m ago`;
	const diffHours = Math.floor(diffMins / 60);
	if (diffHours < 24) return `${diffHours}h ago`;
	return formatDate(timestamp);
}

function PlusIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function InfoIcon() {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<line x1="12" y1="16" x2="12" y2="12" />
			<line x1="12" y1="8" x2="12.01" y2="8" />
		</svg>
	);
}

function CopyIcon() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
			<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
		</svg>
	);
}

function CloseIcon() {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="18" y1="6" x2="6" y2="18" />
			<line x1="6" y1="6" x2="18" y2="18" />
		</svg>
	);
}
